import { useEffect, useState } from 'react';
import { CircleHelp, SquareCheck, XSquare } from 'lucide-react';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { VerificationCriteriaStatus } from '@/backend/access/verificationCriteria/types';
import { I18n } from '@/i18n/I18n';
import { AddVerificationCriteriaButton } from './AddVerificationCriteriaButton';
import { Dropdown } from 'react-bootstrap';
import { CustomToggle } from '../dropdown/CustomToggle';
import { DeleteItem } from '../dropdown/DeleteItem';
import { VerificationCriteriaPanel } from './VerificationCriteriaPanel';
import { VerificationCriteria } from '@/backend/models/verificationCriteria';

interface VerificationCriteriaTableProps {
  requirementId: string;
}

export function VerificationCriteriaTable({ requirementId }: VerificationCriteriaTableProps) {
  const fetchVerificationCriteria = useRequirementsStore(state => state.fetchVerificationCriteria);
  const fetchVerificationCriteriaTasksByRequirementId = useRequirementsStore(state => state.fetchVerificationCriteriaTasksByRequirementId);

  useEffect(() => {
    void fetchVerificationCriteria(requirementId);
    void fetchVerificationCriteriaTasksByRequirementId(requirementId);
  }, [requirementId, fetchVerificationCriteria, fetchVerificationCriteriaTasksByRequirementId]);

  return (
    <div className="list-card mt-4" style={{ width: '900px' }}>
      <div className="list-card-title">{I18n.get('verificationCriteria')}</div>
      <VerificationCriteriaBody requirementId={requirementId} />
    </div>
  );
}

function VerificationCriteriaBody({ requirementId }: { requirementId: string }) {
  const verificationCriteria = useRequirementsStore(state => state.verificationCriteria);

  if(verificationCriteria.length === 0) {
    return (
      <div className="empty-list-card-content">
        <AddVerificationCriteriaButton requirementId={requirementId} btnSize="medium" />
        <p className="list-card-help-text">{I18n.get('addVerificationCriteriaHelperText')}</p>
      </div>
    );
  }

  return (
    <>
      <table className="fusa-table mb-2" aria-label={I18n.get('verificationCriteria')}>
        <thead>
          <tr className='fusa-table-header'>
            <th>{I18n.get('title')}</th>
            <th>{I18n.get('description')}</th>
            <th>{I18n.get('acceptance')}</th>
            <th/>
          </tr>
        </thead>
        <tbody>
          {verificationCriteria.map(vc => (
            <VerificationCriteriaRow key={vc.id} verificationCriteria={vc} requirementId={requirementId} />
          ))}
        </tbody>
      </table>
      <AddVerificationCriteriaButton requirementId={requirementId} btnSize="small" />
    </>
  );
}

function VerificationCriteriaRow({ verificationCriteria, requirementId }: { verificationCriteria: VerificationCriteria, requirementId: string }) {
  const [showEditPanel, setShowEditPanel] = useState(false);
  const deleteVerificationCriteria = useRequirementsStore(state => state.deleteVerificationCriteria);

  return (
    <tr className="fusa-table-row">
      <td className="fusa-table-cell-text">{verificationCriteria.title}</td>
      <td className="fusa-table-cell-text">{verificationCriteria.description}</td>
      <td className='text-center'><StatusIcon status={verificationCriteria.status} /></td>
      <td>
        <Dropdown drop="down">
          <Dropdown.Toggle size="sm" as={CustomToggle('verificationCriteria')} variant="dark" id={`verificationCriteria-dropdown-${verificationCriteria.id}`} />
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setShowEditPanel(true)}>
              {I18n.get('edit')}
            </Dropdown.Item>
            <DeleteItem deleteAction={() => deleteVerificationCriteria(verificationCriteria.id)} itemType="verificationCriteria" title={verificationCriteria.title} />
          </Dropdown.Menu>
        </Dropdown>
      </td>
      {showEditPanel && <VerificationCriteriaPanel
        onHide={() => setShowEditPanel(false)}
        requirementId={requirementId}
        verificationCriteria={verificationCriteria}
      />}
    </tr>
  );
}

function StatusIcon({ status }: { status: VerificationCriteriaStatus }) {
  switch (status) {
    case VerificationCriteriaStatus.ACCEPTED:
      return <SquareCheck size={20} className="text-success" />;
    case VerificationCriteriaStatus.REJECTED:
      return <XSquare size={20} className="text-danger" />;
    case VerificationCriteriaStatus.MISSING_EVIDENCE:
      return <CircleHelp size={20} color="var(--neutral-400)" />;
    default:
      return <span className="text-muted">--</span>;
  }
}
